/**
 * Экран заявки (сценарий 3 docs/spec.md): секции «Организация», «Спортсмены»,
 * «Дуйлянь», «Представители», «Гостиница и трансфер от организаторов», а под ними —
 * чекбокс подтверждения участия (сценарий 6).
 *
 * Данные: источник правды — ответ сервера, экран держит последнюю заявку и
 * обновляет её после каждого запроса. Правку завершают кнопки «галочка» и
 * «крестик»: галочка записывает строку или секцию (новую строку — создаёт),
 * крестик отменяет правку и не отправляет запрос. Пока правка идёт, в API не
 * уходит ничего — иначе «крестик» не смог бы её отменить. Общей кнопки
 * «Сохранить» у формы нет: запись начинается галочкой конкретной правки.
 * Автосохранением остаётся только поле уровня секции, у которого своей правки нет
 * («Количество остальных сопровождающих»).
 *
 * Секции сворачиваются нативным <details>, режимы «просмотр/редактирование»
 * переключаются классом .is-editing (стили — styles/components.css). Разметка
 * собирается только хелперами dom.ts (правило задачи 5).
 */
import {
  DISCIPLINES,
  DOCUMENTS,
  DUILIAN_DISCIPLINES,
  GENDER_LABELS,
  GENDERS,
  STATIONS,
  canOrderTransfer,
  hasDuilianDiscipline,
  stationName,
  TSHIRT_SIZES,
} from '../shared/dictionaries';
import type {
  Application,
  Athlete,
  AthleteDocument,
  DisciplineId,
  DocumentId,
  DuilianEntry,
  DuilianParticipant,
  EventInfo,
  Gender,
  OrganizationInfo,
  PublicUser,
  Representative,
  Transfer,
  TransferKind,
  TshirtSize,
} from '../shared/types';
import * as api from './api';
import { ApiError } from './api';
import { userMasthead } from './accountMenu';
import {
  button,
  checkboxField,
  el,
  field,
  iconButton,
  messageBox,
  renderScreen,
} from './dom';
import type { FieldControl, MessageBox } from './dom';
import { APPLICATION_HREF, navigate } from './router';
import { getSessionToken, saveSession } from './session';
import {
  PUBLISHED_NOTE,
  autosave,
  cell,
  checkboxControl,
  confirmDialog,
  controlValue,
  emptyCell,
  errorText,
  fieldsSection,
  flushOnFocusOut,
  inputControl,
  saveStatus,
  sectionFrame,
  selectControl,
  showLoadError,
  tableHead,
  textControl,
  type SectionField,
  type SelectOption,
} from './ui';

/** Идентификатор строки, которой ещё нет на сервере (черновик новой строки). */
const ADD_ROW_ID = 'new-row';

/** Подпись пустого варианта в дропдауне вида программы: строку добавили, выбор не сделан. */
const DISCIPLINE_PLACEHOLDER = '— выберите вид программы —';

/** Контекст экрана: текущая заявка (ответ сервера) и общие обработчики ошибок. */
interface ScreenContext {
  /** Последняя заявка с сервера. */
  application(): Application;
  /** Запоминает ответ сервера. */
  apply(application: Application): void;
  /** Общая ошибка экрана: сбой загрузки или истёкшая сессия. */
  fail(err: unknown): void;
}

/** Открывает экран заявки: загружает заявку и рисует секции. */
export function showApplicationScreen(user: PublicUser): void {
  const message = messageBox();
  const sections = el('div', { className: 'application' });
  renderScreen(
    'Заявка',
    el('div', { className: 'application-screen', children: [message.root, sections] }),
    userMasthead(user),
  );

  message.show('Загружаем заявку…', 'info');
  api
    .getApplication()
    .then(({ application, event }) => {
      message.clear();
      buildSections(sections, application, event, message);
    })
    .catch((err: unknown) => {
      // 409 у загрузки заявки ровно один: название организации из профиля уже занято
      // заявкой другого пользователя (одна активная заявка на организацию). Вместо
      // тупиковой ошибки показываем экран, на котором название можно исправить.
      if (err instanceof ApiError && err.status === 409) {
        showTakenOrganizationScreen(user);
        return;
      }
      showLoadError(err, message);
    });
}

/**
 * Экран «название организации занято» (задача 29) — спасение для пользователя, который
 * зарегистрировался с названием, уже занятым заявкой другого тренера.
 *
 * Почему экран нужен. Заявка создаётся при первом открытии формы и «занимает»
 * название организации; второй тренер с тем же названием получает 409 и без этого
 * экрана остался бы с вечной ошибкой загрузки — название живёт в его профиле, а формы
 * правки профиля в сервисе нет.
 *
 * Что делает экран: объясняет положение, предлагает название с добавленным «(2)»
 * (обычный случай — филиал или вторая команда той же организации) и меняет название в
 * профиле (`PATCH /api/profile/organization`). Сервер проверяет новое название на
 * уникальность, поэтому занятое название показывает ошибку у поля; после успеха сессия
 * обновляется и форма заявки открывается заново — теперь её создание проходит.
 */
function showTakenOrganizationScreen(user: PublicUser): void {
  const message = messageBox();

  const input = el('input', {
    attrs: { type: 'text', name: 'organizationName', value: `${user.organizationName} (2)` },
  });
  const nameField = field({
    id: 'taken-organization-name',
    label: 'Название организации',
    control: input,
  });
  const submit = button({ text: 'Внести изменение', variant: 'primary', type: 'submit' });

  const form = el('form', {
    className: 'card',
    children: [
      el('p', {
        text: `Другой пользователь уже подал заявку на организацию «${user.organizationName}».`,
      }),
      el('ul', {
        className: 'notice__list',
        children: [
          el('li', { text: 'Если Вы хотите отредактировать ее - войдите как автор заявки.' }),
          el('li', {
            text:
              'Если Вы хотите подать собственную заявку, то отредактируйте название Вашей ' +
              'организации, например добавьте название филиала.',
          }),
        ],
      }),
      nameField.root,
      el('div', { className: 'form-actions', children: [submit] }),
    ],
  });

  renderScreen(
    'Организация уже занята',
    el('div', { className: 'application-screen', children: [message.root, form] }),
    userMasthead(user),
  );

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const organizationName = input.value.trim();
    if (organizationName === '') {
      nameField.setError('Название организации обязательно');
      return;
    }
    nameField.clearError();
    try {
      const { user: updated } = await api.updateProfileOrganization(organizationName);
      // Профиль в сессии должен совпадать с хранилищем: по нему открывается форма
      // заявки после перезагрузки страницы, и он же показывается в мастхеде.
      const token = getSessionToken();
      if (token) saveSession(token, updated);
      // Экран заявки рисуется заново из общего источника выбора экрана: так он
      // загрузится с новым профилем, а не с закешированным старым.
      navigate(APPLICATION_HREF);
    } catch (err) {
      // Занятое название — ошибка у поля: видно, что исправлять нужно именно его.
      // Остальные сбои (нет соединения, 502) показываются сообщением экрана.
      if (err instanceof ApiError && err.status === 409) {
        nameField.setError(err.message);
        return;
      }
      message.show(errorText(err), 'error');
    }
  });
}

/**
 * Собирает секции формы; модель — последний ответ сервера. Порядок блоков такой же,
 * как в спеке: сначала секции заявки, последним — чекбокс подтверждения участия
 * (подтверждается заявка целиком, поэтому он стоит под всеми секциями).
 */
function buildSections(
  root: HTMLElement,
  initial: Application,
  event: EventInfo,
  message: MessageBox,
): void {
  let current = initial;
  const context: ScreenContext = {
    application: () => current,
    apply: (application) => {
      current = application;
    },
    fail: (err) => showLoadError(err, message),
  };

  root.replaceChildren(
    organizationSection(context),
    athletesSection(context),
    duilianSection(context),
    representativesSection(context),
    transfersSection(context),
    confirmBlock(context, event),
  );
}

/** Подпись чекбокса подтверждения: турнир, город и сроки — из конфигурации мероприятия. */
function confirmLabelText(event: EventInfo): string {
  const tournament = [event.name, event.city, event.dates].join(', ');
  return `Подтверждаю участие нашей команды в предстоящем турнире: ${tournament}`;
}

/** Подпись кнопки, публикующей заявку: галка её только разблокирует. */
const CONFIRM_BUTTON_TEXT = 'Подтвердить';

/** Инструкция к черновику: пока заявку не подтвердили, организаторы её не видят. */
const DRAFT_NOTE =
  'Сейчас заявка находится в режиме автоматически сохраняемого черновика. ' +
  'Организаторы её не видят. Чтобы опубликовать заявку, проставьте галку и нажмите ' +
  'кнопку «Подтвердить».';

/**
 * Блок подтверждения участия в конце формы (сценарий 6 docs/spec.md).
 *
 * Это не секция формы: подтверждается заявка целиком, поэтому у блока нет ни режима
 * просмотра/редактирования, ни автосохранения по таймеру. Пока заявка не подтверждена,
 * блок объясняет, что она лежит черновиком и организаторам не видна, а отметка чекбокса
 * сама ничего не отправляет: галка только разблокирует кнопку «Подтвердить» — запрос
 * `POST /api/application/confirm` уходит по её нажатию. Подпись чекбокса собирается из
 * описания мероприятия, которое приходит вместе с заявкой (поля конфигурации `eventName`,
 * `eventCity`, `eventDates`).
 *
 * У подтверждённой заявки чекбокс отмечен и выключен: снять признак может только админское
 * «Обнулить все подтверждения» (сценарий 7), поэтому форма не даёт отменить подтверждение.
 * Вместо кнопки блок показывает текст о том, что заявка опубликована и её правки видны
 * организаторам в реальном времени.
 *
 * Разметку даёт `checkboxField` (`.field--checkbox`) — как у согласий в форме регистрации:
 * флажок стоит **первым**, подпись идёт справа и переносится по строкам, поэтому длинное
 * название турнира не ломает карточную раскладку на телефоне.
 */
function confirmBlock(context: ScreenContext, event: EventInfo): HTMLElement {
  /** Идёт запрос подтверждения: контролы выключены, второй отправки не будет. */
  let pending = false;

  const indicator = el('span', { className: 'save-indicator', attrs: { 'aria-live': 'polite' } });
  const checkbox = el('input', { attrs: { type: 'checkbox' } });
  const fieldNode = checkboxField({
    id: 'application-confirmed',
    control: checkbox,
    label: [confirmLabelText(event)],
  });
  const note = el('p', { className: 'confirm__note', text: DRAFT_NOTE });
  const submit = button({
    text: CONFIRM_BUTTON_TEXT,
    variant: 'success',
    onClick: () => void confirm(),
  });
  submit.classList.add('btn--lg');
  const actions = el('div', { className: 'confirm__actions', children: [submit] });
  const published = el('p', { className: 'confirm__published', text: PUBLISHED_NOTE });
  const root = el('div', {
    className: 'confirm',
    children: [
      note,
      el('div', { className: 'confirm__row', children: [fieldNode.root, indicator] }),
      actions,
      published,
    ],
  });

  /** Приводит блок к состоянию заявки (последний ответ сервера). */
  function render(): void {
    const confirmed = context.application().confirmed;
    // Подтверждённая заявка показывается отмеченной выключенной галкой: снять признак
    // из формы нельзя. Пока идёт запрос, выключены оба контрола.
    if (confirmed) checkbox.checked = true;
    checkbox.disabled = confirmed || pending;
    // Кнопку разблокирует галка: без неё подтверждать нечего.
    submit.disabled = pending || !checkbox.checked;
    // Черновик: инструкция и кнопка «Подтвердить». Публикация: вместо кнопки — текст.
    note.hidden = confirmed;
    actions.hidden = confirmed;
    published.hidden = !confirmed;
  }

  /** Публикует заявку: признак в заявке и письмо организаторам. */
  async function confirm(): Promise<void> {
    pending = true;
    indicator.className = 'save-indicator';
    indicator.textContent = 'Подтверждаем участие…';
    render();
    try {
      context.apply((await api.confirmApplication()).application);
      indicator.className = 'save-indicator save-indicator--saved';
      indicator.textContent = 'Участие подтверждено';
    } catch (err) {
      // Признак на сервере не выставлен: снимаем галку, иначе форма показывала бы
      // состояние, которого в заявке нет, а кнопка осталась бы доступной.
      checkbox.checked = false;
      if (err instanceof ApiError && err.status === 401) {
        context.fail(err);
        return;
      }
      indicator.className = 'save-indicator save-indicator--error';
      indicator.textContent = errorText(err);
    } finally {
      pending = false;
    }
    render();
  }

  checkbox.addEventListener('change', () => render());
  render();
  return root;
}

/** Поля секции «Организация» в порядке отображения. */
const ORGANIZATION_FIELDS: SectionField<OrganizationInfo>[] = [
  {
    id: 'organization-name',
    label: 'Название организации',
    view: (row) => row.name,
    control: (row) => textControl(row.name),
  },
  {
    id: 'organization-headName',
    label: 'Имя руководителя',
    view: (row) => row.headName,
    control: (row) => textControl(row.headName),
  },
  {
    id: 'organization-email',
    label: 'Email',
    view: (row) => row.email,
    control: (row) => inputControl('email', row.email),
  },
  {
    id: 'organization-phone',
    label: 'Телефон',
    view: (row) => row.phone,
    control: (row) => inputControl('tel', row.phone),
  },
  {
    id: 'organization-city',
    label: 'Город',
    view: (row) => row.city,
    control: (row) => textControl(row.city),
  },
  {
    id: 'organization-country',
    label: 'Страна',
    view: (row) => row.country,
    control: (row) => textControl(row.country),
  },
];

/**
 * Секция «Организация»: просмотр и редактирование полей с автосохранением.
 * Название организации приходит из профиля пользователя, поэтому после загрузки
 * секция открыта в режиме просмотра (в ней уже есть данные).
 */
function organizationSection(context: ScreenContext): HTMLElement {
  return fieldsSection<OrganizationInfo>({
    title: 'Организация',
    editTitle: 'Редактировать организацию',
    fields: ORGANIZATION_FIELDS,
    data: () => context.application().organization,
    // Поля секции идут в порядке ORGANIZATION_FIELDS: позиция значения = позиция поля.
    fromValues: (values) => ({
      name: values[0],
      headName: values[1],
      email: values[2],
      phone: values[3],
      city: values[4],
      country: values[5],
    }),
    // Сохранённую секцию запоминает модель экрана: сервер отдаёт заявку целиком.
    save: async (row) => {
      context.apply((await api.saveOrganization(row)).application);
    },
    fail: context.fail,
  });
}

/** Подписи подсекций секции «Гостиница и трансфер от организаторов». */
const TRANSFERS_TITLE = 'Гостиница и трансфер от организаторов';

/**
 * Подсекции секции: ключ поля заявки, заголовок и признак колонки «Раннее заселение».
 * Колонка есть только у «Приезда»: раннее заселение — свойство строки приезда
 * (решение организатора 15.10.2026).
 */
const TRANSFER_SUBSECTIONS: { kind: TransferKind; title: string; earlyCheckIn: boolean }[] = [
  { kind: 'arrival', title: 'Приезд', earlyCheckIn: true },
  { kind: 'departure', title: 'Отъезд', earlyCheckIn: false },
];

/** Подпись пустого варианта в дропдауне станций: вокзал или аэропорт не выбран. */
const TRANSFER_STATION_PLACEHOLDER = '— выберите вокзал или аэропорт —';

/** Варианты дропдауна вокзалов и аэропортов: пустой вариант + справочник станций. */
function stationOptions(): SelectOption[] {
  return [
    { value: '', label: TRANSFER_STATION_PLACEHOLDER },
    ...STATIONS.map((station) => ({ value: station.id, label: station.name })),
  ];
}

/**
 * Позиции колонок строки приезда/отъезда в массиве значений `rowsSection` (см.
 * `fromValues`) и в массиве контролов (`linkRow`): вынесены в константы, потому что
 * вокзал и «Трансфер нужен» читает ещё и связка контролов — иначе индексы разошлись бы
 * при добавлении колонки.
 */
const TRANSFER_STATION_COLUMN = 3;
const TRANSFER_NEEDED_COLUMN = 4;
const TRANSFER_TRAIN_COLUMN = 5;
/** Колонка «Раннее заселение»: у «Отъезда» её нет, поэтому в его строке значения нет. */
const TRANSFER_EARLY_CHECK_IN_COLUMN = 6;

/**
 * Чекбокс «Трансфер нужен»: отметить его можно только у строки с выбранным вокзалом или
 * аэропортом. У «Самостоятельно» и у строки без вокзала встречать команду негде, поэтому
 * флажок выключен (правка организатора 19.10.2026). Правило общее с бэкендом —
 * `canOrderTransfer`.
 */
function transferNeededControl(row: Transfer): FieldControl {
  const control = checkboxControl(row.transferNeeded);
  control.disabled = !canOrderTransfer(row.stationId);
  return control;
}

/**
 * Связывает контролы строки: выбранный вокзал включает чекбокс «Трансфер нужен» и
 * отмечает его (по умолчанию команду встречают), «Самостоятельно» и пустой вариант —
 * снимают и выключают: встречать негде. Флажок остаётся редактируемым, поэтому снять
 * его у строки с вокзалом пользователь может — вокзал выбран, а трансфер не нужен.
 */
function linkTransferControls(controls: (FieldControl | null)[]): void {
  const station = controls[TRANSFER_STATION_COLUMN];
  const transferNeeded = controls[TRANSFER_NEEDED_COLUMN];
  if (!(station instanceof HTMLSelectElement)) return;
  if (!(transferNeeded instanceof HTMLInputElement)) return;

  station.addEventListener('change', () => {
    const needed = canOrderTransfer(station.value);
    transferNeeded.disabled = !needed;
    transferNeeded.checked = needed;
  });
}

/** Число из значения поля: очищенное поле — 0, остальное проверит сервер. */
function numberFromText(value: string): number {
  return value === '' ? 0 : Number(value);
}

/**
 * Колонки строки приезда/отъезда: состав один у обеих подсекций, отличает их только
 * колонка «Раннее заселение» у «Приезда». Значения колонок собираются из строки
 * таблицы (`rowsSection`), поэтому новых полей заявки эти колонки не вводят.
 */
function transferColumns(withEarlyCheckIn: boolean): RowColumn<Transfer>[] {
  const columns: RowColumn<Transfer>[] = [
    {
      label: 'Дата',
      view: (row) => textView(row.date),
      control: (row) => dateControl(row.date),
    },
    {
      label: 'Время',
      view: (row) => textView(row.time),
      control: (row) => timeControl(row.time),
    },
    {
      label: 'Количество человек',
      // Ноль — это «не указано»: пустое поле формы и пустая строка равнозначны.
      view: (row) => textView(row.peopleCount === 0 ? '' : String(row.peopleCount)),
      control: (row) => numberControl(row.peopleCount),
    },
    {
      label: 'Вокзал / аэропорт',
      view: (row) => textView(stationName(row.stationId)),
      control: (row) => selectControl(stationOptions(), row.stationId),
    },
    {
      // Колонка идёт сразу за вокзалом: флажок поднимается и выключается выбором вокзала.
      label: 'Трансфер нужен',
      view: (row) => textView(row.transferNeeded ? 'Да' : 'Нет'),
      control: (row) => transferNeededControl(row),
    },
    {
      label: 'Номер поезда / рейса',
      view: (row) => textView(row.trainOrFlightNumber),
      control: (row) => textControl(row.trainOrFlightNumber),
    },
  ];
  if (withEarlyCheckIn) {
    columns.push({
      label: 'Раннее заселение',
      view: (row) => textView(row.earlyCheckIn ? 'Да' : 'Нет'),
      control: (row) => checkboxControl(row.earlyCheckIn),
    });
  }
  return columns;
}

/**
 * Секция «Гостиница и трансфер от организаторов» (сценарий 3 docs/spec.md): две
 * подсекции-таблицы «Приезд» и «Отъезд». Команда приезжает и уезжает частями,
 * поэтому каждая часть — своя строка, а не одно значение на команду (решение
 * организатора 15.10.2026).
 *
 * Режима просмотра/редактирования у секции нет: его переключает каждая строка (как
 * у «Спортсменов»), поэтому и индикатор сохранения свой у каждой подсекции — строки
 * сохраняются разными запросами (`/api/transfers/arrival|departure`). Раскладку
 * подсекций строит общая фабрика `rowsSection` в режиме подсекции.
 */
function transfersSection(context: ScreenContext): HTMLElement {
  const body = el('div', {
    className: 'section__body',
    children: TRANSFER_SUBSECTIONS.map((item) => transferSubsection(context, item)),
  });
  // Индикатор секции не нужен: он есть у каждой подсекции. Пустой <span> в заголовке
  // невидим (стили .save-indicator — только цвет и размер шрифта), поэтому разметку
  // секции даёт та же фабрика `sectionFrame`.
  const { root } = sectionFrame(TRANSFERS_TITLE, body);
  return root;
}

/** Подсекция «Приезд»/«Отъезд»: таблица строк со своим CRUD и своим индикатором. */
function transferSubsection(
  context: ScreenContext,
  item: { kind: TransferKind; title: string; earlyCheckIn: boolean },
): HTMLElement {
  const { kind } = item;
  return rowsSection<Transfer>(context, {
    title: item.title,
    addTitle: `Добавить ${item.title.toLowerCase()}`,
    columns: transferColumns(item.earlyCheckIn),
    subsection: true,
    // В карточке под ячейкой действий только иконки — как у спортсменов.
    hideActionsLabel: true,
    rows: (screen) => screen.application()[kind],
    draft: () => ({
      id: ADD_ROW_ID,
      date: '',
      time: '',
      peopleCount: 0,
      stationId: '',
      // Вокзала у новой строки нет, поэтому и трансфер ей пока не нужен: флажок
      // поднимется сам, когда пользователь выберет вокзал или аэропорт.
      transferNeeded: false,
      trainOrFlightNumber: '',
      earlyCheckIn: false,
    }),
    fromValues: (values, existing) => ({
      id: existing.id,
      date: values[0],
      time: values[1],
      peopleCount: numberFromText(values[2]),
      stationId: values[TRANSFER_STATION_COLUMN],
      transferNeeded: values[TRANSFER_NEEDED_COLUMN] === 'true',
      trainOrFlightNumber: values[TRANSFER_TRAIN_COLUMN],
      // Колонки «Раннее заселение» у «Отъезда» нет: значение остаётся исходным.
      earlyCheckIn: values[TRANSFER_EARLY_CHECK_IN_COLUMN] === 'true',
    }),
    // Вокзал и «Трансфер нужен» связаны: выбор вокзала поднимает и выключает флажок.
    linkRow: linkTransferControls,
    create: async (row) => (await api.createTransfer(kind, row)).application,
    update: async (row) => (await api.updateTransfer(kind, row)).application,
    remove: async (id) => (await api.deleteTransfer(kind, id)).application,
  });
}

/**
 * Колонка таблицы строк заявки.
 */
interface RowColumn<T> {
  /** Подпись столбца: заголовок таблицы и подпись карточки на узком экране. */
  label: string;
  /** Содержимое ячейки в режиме просмотра. */
  view(row: T, view: RowViewContext): Node;
  /** Контрол редактирования; у вычисляемых колонок (виды, документы) его нет. */
  control?(row: T): FieldControl;
}

/**
 * Окружение ячейки режима просмотра: счётчики открывают диалоги, поэтому ячейке
 * нужны модель экрана (последний ответ сервера) и перерисовка строк секции.
 */
interface RowViewContext {
  /** Модель экрана: диалог берёт из неё данные и кладёт в неё ответы сервера. */
  screen: ScreenContext;
  /** Перерисовывает строки секции: счётчик показывает сохранённое значение. */
  refresh(): void;
}

/**
 * Числовое поле уровня секции со списком строк: то, что относится к секции целиком,
 * а не к её строкам (у «Представителей» — количество остальных сопровождающих).
 * Поле стоит под таблицей и всегда открыто для ввода: у секции-таблицы нет общего
 * режима просмотра/редактирования (каждая строка переключает его сама), а один
 * «карандаш» ради одного поля дублировал бы эти режимы. Своей правки у поля нет,
 * поэтому записать его «галочкой» нельзя — оно сохраняется автосохранением по
 * таймеру и при уходе фокуса (единственное такое поле среди секций заявки).
 */
interface RowsSectionNumberField {
  /** Идентификатор контрола: по нему <label> связывает подпись с полем. */
  id: string;
  /** Подпись поля. */
  label: string;
  /** Значение поля из модели экрана (последний ответ сервера). */
  value(application: Application): number;
  /** Автосохранение значения; возвращает заявку целиком. */
  save(value: number): Promise<Application>;
}

/** Параметры секции со списком строк: «Спортсмены», «Дуйлянь», «Представители» и подсекции приезда/отъезда. */
interface RowsSectionOptions<T extends { id: string }> {
  title: string;
  /** Подпись кнопки «+» (доступное имя для скринридера и всплывающей подсказки). */
  addTitle: string;
  columns: RowColumn<T>[];
  /**
   * Строки секции — часть другой секции, а не самостоятельная секция формы (так
   * живут «Приезд» и «Отъезд» внутри «Гостиницы и трансфера от организаторов»).
   * Тогда вместо своей <details> строится блок «заголовок + индикатор + таблица»:
   * у подсекции нет ни свёртывания, ни общего режима правки — его переключает
   * каждая строка, как в обычной секции-таблице.
   */
  subsection?: boolean;
  /**
   * Числовое поле секции (не строки) под таблицей; у секций без такого поля его нет.
   * Необязательное — «Спортсмены» и «Дуйлянь» обходятся колонками строк.
   */
  numberField?: RowsSectionNumberField;
  /**
   * Не показывать подпись ячейки действий в карточке (модификатор
   * `table--no-actions-label`): в ячейке только иконки, и подпись «Действия» отнимала
   * у карточки строку. В широкой раскладке подпись столбца берётся из шапки таблицы.
   */
  hideActionsLabel?: boolean;
  /**
   * Подсказка в подвале секции — мелкий текст под таблицей и кнопкой «+»: правило,
   * которое из самих полей не видно (в «Дуйляне» — кого показывает дропдаун
   * спортсмена). У секций без такого пояснения подсказки нет.
   */
  hint?: string;
  /**
   * Связывает контролы строки в правке: колонки видят друг друга (в «Дуйляне»
   * дропдауны спортсменов закрывают уже выбранные в этой же строке ФИО). Позиции
   * массива совпадают с позициями колонок; у вычисляемой колонки — null.
   */
  linkRow?(controls: (FieldControl | null)[]): void;
  /** Текущие строки заявки. */
  rows(context: ScreenContext): T[];
  /** Пустая строка-черновик для кнопки «+» (id = ADD_ROW_ID). */
  draft(): T;
  /**
   * Собирает строку из значений колонок (в порядке columns); `existing` — строка,
   * которую правят (у новой строки он равен черновику из `draft`). Поля, которых нет
   * среди колонок (виды программы), берутся из `existing`: правка строки их не теряет.
   */
  fromValues(values: string[], existing: T): T;
  /** Запросы к API: возвращают заявку целиком — экран обновляет модель из ответа. */
  create(row: T): Promise<Application>;
  update(row: T): Promise<Application>;
  remove(id: string): Promise<Application>;
}

/**
 * Секция со списком строк (спортсмены, «Дуйлянь», представители, приезды, отъезды):
 * таблица с режимами просмотра и редактирования строки, кнопка «+» и запись по
 * «галочке». Пока строка правится, в API не уходит ничего, поэтому «крестик»
 * действительно отменяет правку. Новая строка живёт только на экране до «галочки»
 * (у неё ещё нет id).
 */
function rowsSection<T extends { id: string }>(
  context: ScreenContext,
  options: RowsSectionOptions<T>,
): HTMLElement {
  const body = el('div', { className: 'section__body' });
  const { root, indicator, setTitle } = sectionFrame(options.title, body, {
    subsection: options.subsection,
  });
  const status = saveStatus(context.fail, indicator);
  const tbody = el('tbody');
  const table = el('table', {
    className: options.hideActionsLabel ? 'table table--no-actions-label' : 'table',
    children: [tableHead([...options.columns.map((column) => column.label), 'Действия']), tbody],
  });

  /** id строки в режиме редактирования (черновик новой строки — ADD_ROW_ID). */
  let editingId: string | null = null;
  /** Строка, к которой относятся контролы редактирования. */
  let editingRow: T | null = null;
  /** Контролы редактируемой строки: позиция в массиве = позиция колонки. */
  let controls: (FieldControl | null)[] = [];

  /**
   * Контрол числового поля секции (см. `sectionFieldNode`) и его автосохранение:
   * поле относится к секции, поэтому у него свой таймер, а индикатор — общий с
   * секцией, как у остальных автосохранений.
   */
  let fieldControl: FieldControl | null = null;
  const pendingField = autosave(() => void saveField());

  /** Окружение ячеек просмотра: модель экрана и перерисовка строк (диалоги счётчиков). */
  const viewContext: RowViewContext = { screen: context, refresh: render };

  /** Значения колонок редактируемой строки. */
  function readValues(): string[] {
    return options.columns.map((_, index) => controlValue(controls[index]));
  }

  /**
   * Числовое поле секции (не строки) под таблицей: подпись и контрол, всегда открытый
   * для ввода. Значение берётся из модели экрана; обратно контрол не перерисовывается —
   * поле меняет только пользователь, а отклонённое сервером значение остаётся в поле
   * вместе с сообщением об ошибке в индикаторе секции (как у правки строки).
   * Секции без такого поля получают null.
   */
  function sectionFieldNode(): HTMLElement | null {
    const settings = options.numberField;
    if (!settings) return null;
    fieldControl = numberControl(settings.value(context.application()));
    fieldControl.addEventListener('input', () => pendingField.schedule());
    return el('div', {
      className: 'row-edit__fields section-field',
      children: [field({ id: settings.id, label: settings.label, control: fieldControl }).root],
    });
  }

  /**
   * Автосохранение числового поля секции (дебаунс и уход фокуса): поле всегда
   * открыто для ввода, своей правки с «галочкой» у него нет — так оно и остаётся
   * единственным автосохраняемым полем секции со списком строк.
   */
  async function saveField(): Promise<void> {
    const settings = options.numberField;
    if (!settings || !fieldControl) return;
    status.pending();
    try {
      context.apply(await settings.save(numberFromText(controlValue(fieldControl))));
      status.saved();
    } catch (err) {
      status.error(err);
    }
  }

  /** Галочка: записывает строку — существующую обновляет, новую создаёт. */
  async function acceptRow(): Promise<void> {
    if (!editingRow) return;
    const row = options.fromValues(readValues(), editingRow);
    status.pending();
    try {
      const application =
        row.id === ADD_ROW_ID ? await options.create(row) : await options.update(row);
      context.apply(application);
      status.saved();
      finishEditing();
    } catch (err) {
      status.error(err);
    }
  }

  /** Удаляет строку после подтверждения диалогом. */
  async function removeRow(row: T): Promise<void> {
    const confirmed = await confirmDialog('Удалить эту строку?', 'Удалить');
    if (!confirmed) return;

    status.pending();
    try {
      context.apply(await options.remove(row.id));
      status.saved();
      finishEditing();
    } catch (err) {
      status.error(err);
    }
  }

  /**
   * Завершает редактирование и перерисовывает строки: несохранённые значения
   * теряются — так «крестик» отменяет правку.
   */
  function finishEditing(): void {
    editingId = null;
    editingRow = null;
    controls = [];
    // Ошибка относилась к правке этой строки: правка закончилась — ошибку снимаем.
    status.clearError();
    render();
  }

  /** Начинает редактирование существующей строки. */
  function startEditing(row: T): void {
    // Ошибка относилась к другой строке, к этой правке она не относится.
    status.clearError();
    editingId = row.id;
    editingRow = row;
    render();
  }

  /** Кнопка «+»: добавляет строку в режиме редактирования (сохранится по «галочке»). */
  function addRow(): void {
    // Ошибка относилась к другой строке, к новой правке она не относится.
    status.clearError();
    const blank = options.draft();
    editingId = blank.id;
    editingRow = blank;
    render();
  }

  /** Перерисовывает строки таблицы из модели заявки (и черновик новой строки). */
  function render(): void {
    const rows = options.rows(context);
    // Заголовок показывает количество строк заявки: оно видно и в свёрнутой секции.
    // Считаем только сохранённые строки: черновик новой строки сущностью ещё не стал.
    setTitle(`${options.title}: ${rows.length}`);
    const nodes = rows.map((row) => renderRow(row));
    const draftRow = editingRow && editingRow.id === ADD_ROW_ID ? renderRow(editingRow) : null;
    tbody.replaceChildren(...nodes, ...(draftRow ? [draftRow] : []));
  }

  /** Строка таблицы: в редактировании — контролы, иначе — текст и действия. */
  function renderRow(row: T): HTMLTableRowElement {
    return row.id === editingId ? renderEditRow(row) : renderViewRow(row);
  }

  /** Строка в режиме просмотра: статичный текст колонок, карандаш и корзина. */
  function renderViewRow(row: T): HTMLTableRowElement {
    return el('tr', {
      children: [
        ...options.columns.map((column) => cell(column.label, column.view(row, viewContext))),
        cell(
          'Действия',
          el('div', {
            className: 'row-view__actions',
            children: [
              iconButton({
                icon: 'edit',
                title: 'Редактировать строку',
                onClick: () => startEditing(row),
              }),
              iconButton({
                icon: 'trash',
                variant: 'danger',
                title: 'Удалить строку',
                onClick: () => void removeRow(row),
              }),
            ],
          }),
        ),
      ],
    });
  }

  /** Строка в режиме редактирования: контролы колонок, галочка и крестик. */
  function renderEditRow(row: T): HTMLTableRowElement {
    controls = options.columns.map((column) => column.control?.(row) ?? null);

    const cells = options.columns.map((column, index) => {
      const control = controls[index];
      if (!control) {
        // Вычисляемые колонки (виды программы, документы) в редактировании не показываются:
        // в ячейке остаётся пустое место — прочерк-заглушка читался бы как значение строки
        // (правка организатора 20.10.2026).
        return emptyCell(column.label);
      }
      const item = field({ id: `${row.id}-${index}`, label: column.label, control });
      return cell(column.label, item.root);
    });

    // Колонки строки могут зависеть друг от друга («Дуйлянь»: дропдауны спортсменов).
    options.linkRow?.(controls);

    return el('tr', {
      className: 'is-editing',
      children: [
        ...cells,
        cell(
          'Действия',
          el('div', {
            className: 'row-edit__actions',
            children: [
              iconButton({
                icon: 'check',
                title: 'Принять изменения',
                onClick: () => void acceptRow(),
              }),
              iconButton({ icon: 'close', title: 'Отменить изменения', onClick: finishEditing }),
            ],
          }),
        ),
      ],
    });
  }

  const toolbar = el('div', {
    className: 'toolbar',
    children: [iconButton({ icon: 'plus', title: options.addTitle, onClick: addRow })],
  });
  // Числовое поле секции идёт после таблицы с кнопкой «+»: оно относится не к
  // строкам, а к секции целиком, поэтому и стоит отдельным блоком под ними.
  const fieldNode = sectionFieldNode();
  // Подсказка — последний блок тела секции: она объясняет правило секции целиком,
  // поэтому и стоит в её подвале, а не рядом с отдельным полем.
  const hintNode = options.hint ? el('p', { className: 'section__hint', text: options.hint }) : null;
  body.append(table, toolbar, ...(fieldNode ? [fieldNode] : []), ...(hintNode ? [hintNode] : []));
  if (fieldNode) flushOnFocusOut(fieldNode, () => pendingField.flush());
  render();
  return root;
}

/** Текстовая ячейка: пустое значение показываем прочерком. */
function textView(value: string): Node {
  return el('span', { text: value || '—' });
}

/**
 * Счётчик-гиперссылка: показывает количество строк диалога и открывает его.
 * Подпись (`label`) идёт в доступное имя и подсказку: сама кнопка показывает число.
 */
function counterView(value: number, label: string, onClick: () => void): Node {
  return el('button', {
    className: 'link-counter',
    text: String(value),
    attrs: { type: 'button', 'aria-label': `${label}: ${value}`, title: `${label}: ${value}` },
    on: { click: onClick },
  });
}

/** Размер файла для строки диалога: «2,1 МБ», «245 КБ». */
function fileSizeText(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} МБ`;
  return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
}

/** Дата загрузки в местном времени: «12.10.2026, 14:05». */
function uploadedAtText(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Поле даты: календарь даёт нативный <input type="date"> (требование задачи 5). */
function dateControl(value: string): FieldControl {
  return el('input', { attrs: { type: 'date', value } });
}

/** Поле времени: нативный <input type="time"> в 24-часовом формате (ЧЧ:ММ). */
function timeControl(value: string): FieldControl {
  return el('input', { attrs: { type: 'time', value } });
}

/** Числовое поле количества человек: ограничение снизу — 0. */
function numberControl(value: number): FieldControl {
  return el('input', { attrs: { type: 'number', min: 0, value: String(value) } });
}

/**
 * Закрывает в дропдаунах варианты, уже выбранные в других дропдаунах того же списка
 * (спортсмены строки «Дуйляня»): один спортсмен не может выступать в выступлении
 * дважды, и сервер отклоняет повторы. Пустой вариант («не указан») остаётся
 * доступным, а выбранное значение самого дропдауна — выбираемым. Перерисовки нет:
 * фокус остаётся в дропдауне, который меняет пользователь.
 */
function markTakenOptions(selects: HTMLSelectElement[]): void {
  const taken = new Set(selects.map((select) => select.value).filter((value) => value !== ''));
  for (const select of selects) {
    for (const option of select.options) {
      option.disabled =
        option.value !== '' && option.value !== select.value && taken.has(option.value);
    }
  }
}

/** Варианты размеров футболки для дропдауна. */
function tshirtOptions(): SelectOption[] {
  return TSHIRT_SIZES.map((size) => ({ value: size, label: size }));
}

/**
 * Диалог «Виды программы» спортсмена (spec.md, секция «Спортсмены»): таблица, где
 * строка — один вид программы. «+» добавляет строку, «×» удаляет строку, значение
 * выбирается из справочника видов программы. Список сохраняется целиком при каждом
 * изменении (PATCH /api/athletes): добавление, замена и удаление строки — одна
 * операция, промежуточных состояний на сервере не бывает, а модель экрана — ответ
 * сервера. Повторы разрешены: спортсмен может выйти на один и тот же вид несколько
 * раз, и в зачёт идёт лучшая оценка (правка организатора 18.10.2026), поэтому в
 * дропдауне строки всегда весь справочник, а «+» доступна без ограничения — каждая
 * строка это отдельное выступление. Диалог нативный (<dialog>): Esc, backdrop и
 * удержание фокуса из коробки.
 * По закрытии строки секции перерисовываются: счётчик показывает число из модели.
 * Таблица — `.table--inline-actions`: в карточной раскладке строка набрана одной
 * линией (дропдаун и корзина), подписи столбцов и отдельный блок действий не нужны.
 */
function openDisciplinesDialog(screen: ScreenContext, athlete: Athlete, refresh: () => void): void {
  /** Строки таблицы: код вида программы или '' у только что добавленной строки. */
  let rows: DisciplineId[] = [...athlete.disciplines];
  /** Дропдауны строк: по последнему из них возвращается фокус строке, добавленной «+». */
  let selects: HTMLSelectElement[] = [];

  const indicator = el('span', { className: 'save-indicator', attrs: { 'aria-live': 'polite' } });
  const status = saveStatus(screen.fail, indicator);
  const tbody = el('tbody');
  const addButton = iconButton({ icon: 'plus', title: 'Добавить вид программы', onClick: addRow });
  const dialog = el('dialog', {
    className: 'dialog dialog--table',
    children: [
      el('div', {
        className: 'dialog__header',
        children: [el('p', { className: 'dialog__title', text: 'Виды программы' }), indicator],
      }),
      // ФИО спортсмена: заголовок диалога не говорит, к кому относятся виды программы.
      el('p', { className: 'dialog__subtitle', text: athlete.fullName }),
      el('div', {
        className: 'dialog__body',
        children: [
          el('table', {
            className: 'table table--inline-actions',
            children: [tableHead(['Вид программы', 'Действия']), tbody],
          }),
          el('div', { className: 'toolbar', children: [addButton] }),
        ],
      }),
      el('div', {
        className: 'dialog__actions',
        children: [button({ text: 'Закрыть', onClick: () => dialog.close() })],
      }),
    ],
  });

  /**
   * Варианты дропдауна строки — весь справочник видов программы. Один и тот же вид
   * можно выбрать в нескольких строках (повторы — это повторные выступления), поэтому
   * варианты не закрываются. Пустой вариант недоступен: строка сохраняется с выбранным
   * видом программы.
   */
  const disciplineOptions: SelectOption[] = [
    { value: '', label: DISCIPLINE_PLACEHOLDER, disabled: true },
    ...DISCIPLINES.map((discipline) => ({ value: discipline.id, label: discipline.name })),
  ];

  /** Перерисовывает таблицу из локального списка строк. */
  function render(): void {
    selects = [];
    const nodes = rows.map((value, index) => {
      const select = selectControl(disciplineOptions, value);
      select.addEventListener('change', () => onSelectChange(index, select.value));
      selects.push(select);
      return el('tr', {
        children: [
          cell(
            'Вид программы',
            field({ id: `discipline-${index}`, label: 'Вид программы', control: select }).root,
          ),
          cell(
            'Действия',
            el('div', {
              className: 'row-view__actions',
              children: [
                iconButton({
                  icon: 'trash',
                  variant: 'danger',
                  title: 'Удалить вид программы',
                  onClick: () => removeRow(index),
                }),
              ],
            }),
          ),
        ],
      });
    });
    tbody.replaceChildren(...nodes);
    // Ограничения на число строк нет: один и тот же вид программы можно выбрать
    // несколько раз — это отдельные выступления спортсмена.
  }

  /** Выбор вида программы в строке: сохраняем список целиком. */
  function onSelectChange(index: number, value: string): void {
    rows[index] = value;
    void save();
  }

  /** «+»: новая строка с пустым выбором — сохранится, когда выберут вид программы. */
  function addRow(): void {
    status.clearError();
    rows.push('');
    render();
    selects[selects.length - 1]?.focus();
  }

  /** «×»: удаляет строку (сохраняется, если в ней был выбран вид программы). */
  function removeRow(index: number): void {
    const hadValue = rows[index] !== '';
    status.clearError();
    rows.splice(index, 1);
    render();
    if (hadValue) void save();
  }

  /** Виды программы спортсмена из модели экрана (последний ответ сервера). */
  function savedDisciplines(): DisciplineId[] {
    return screen.application().athletes.find((item) => item.id === athlete.id)?.disciplines ?? [];
  }

  /** Сохраняет список строк целиком; ошибка возвращает таблицу к ответу сервера. */
  async function save(): Promise<void> {
    status.pending();
    try {
      const selected = rows.filter((value) => value !== '');
      const { application } = await api.saveAthleteDisciplines(athlete.id, selected);
      screen.apply(application);
      status.saved();
    } catch (err) {
      status.error(err);
      // Список сохраняется целиком, поэтому незавершённых состояний не бывает:
      // показываем последний ответ сервера, а не несохранённую правку.
      rows = [...savedDisciplines()];
      render();
      // Истёкшая сессия уводит на экран входа: диалог за ним оставаться не должен.
      if (err instanceof ApiError && err.status === 401) dialog.close();
    }
  }

  dialog.addEventListener('close', () => {
    dialog.remove();
    // Счётчик в строке спортсмена берёт число видов программы из модели.
    refresh();
  });
  document.body.append(dialog);
  render();
  dialog.showModal();
}

/** Кэш настроек загрузки документов: режим передачи файла и лимиты (задача 11). */
let documentsConfigRequest: Promise<api.DocumentsConfig> | undefined;

/**
 * Настройки загрузки документов: режим (`proxy`/`presign`) и лимиты файла.
 * Запрашиваются один раз на сессию; при сбое кэш сбрасывается, чтобы следующее
 * открытие диалога повторило запрос.
 */
function documentsConfig(): Promise<api.DocumentsConfig> {
  documentsConfigRequest ??= api.getDocumentsConfig().catch((err: unknown) => {
    documentsConfigRequest = undefined;
    throw err;
  });
  return documentsConfigRequest;
}

/** Строка диалога «Документы»: тип из словаря и загруженный файл, если он есть. */
interface DocumentRow {
  docId: DocumentId;
  title: string;
  uploaded?: AthleteDocument;
}

/**
 * Бланки согласия для скачивания — статические PDF рядом со статикой SPA
 * (`src/frontend/`, копируются в dist при сборке). Бланка два, по возрасту спортсмена:
 * до 18 лет согласие дают родители, с 18 лет спортсмен даёт согласие сам. Имена файлов
 * менять нельзя: они фиксированы в build:frontend (package.json).
 */
const PARENTAL_CONSENT_FORM_HREF = 'parental-consent-form.pdf';
const ADULT_CONSENT_FORM_HREF = 'adult-consent-form.pdf';

/**
 * Диалог «Документы» спортсмена (spec.md, секция «Спортсмены»): строка на каждый тип из
 * словаря, поэтому состав строк не зависит от данных заявки. В строке видны название
 * документа, имя файла, его размер и дата загрузки; для строки без файла — «Загрузить»,
 * для строки с файлом — «Скачать», «Заменить» и «×».
 *
 * Файл передаётся так, как решил сервер: в разработке — через бэкенд (`proxy`), в
 * облаке — по подписанной ссылке (`presign`) — см. docs/tasks/11-documents-dialog.md.
 * Лимиты берутся из настроек сервера, поэтому проверка на клиенте совпадает с серверной.
 */
function openDocumentsDialog(screen: ScreenContext, athlete: Athlete, refresh: () => void): void {
  const indicator = el('span', { className: 'save-indicator', attrs: { 'aria-live': 'polite' } });
  const status = saveStatus(screen.fail, indicator);
  const tbody = el('tbody');
  const dialog = el('dialog', {
    className: 'dialog dialog--table dialog--documents',
    children: [
      el('div', {
        className: 'dialog__header',
        children: [el('p', { className: 'dialog__title', text: 'Документы' }), indicator],
      }),
      el('p', { className: 'dialog__subtitle', text: athlete.fullName }),
      el('div', {
        className: 'dialog__body',
        children: [
          el('table', {
            className: 'table table--no-actions-label',
            children: [
              tableHead(['Название документа', 'Имя файла', 'Размер и дата', 'Действия']),
              tbody,
            ],
          }),
        ],
      }),
      el('div', {
        className: 'dialog__actions',
        children: [
          // Ссылки на бланки согласия: два статических PDF рядом со статикой SPA.
          // Идут одна под другой (.dialog__links в components.css).
          el('div', {
            className: 'dialog__links',
            children: [
              el('a', {
                className: 'link',
                text: 'Скачать бланк согласия родителей (до 18)',
                attrs: { href: PARENTAL_CONSENT_FORM_HREF, download: true },
              }),
              el('a', {
                className: 'link',
                text: 'Скачать бланк согласия совершеннолетних (18+)',
                attrs: { href: ADULT_CONSENT_FORM_HREF, download: true },
              }),
            ],
          }),
          button({ text: 'Закрыть', onClick: () => dialog.close() }),
        ],
      }),
    ],
  });

  /** Загруженные документы спортсмена из модели экрана (последний ответ сервера). */
  function savedDocuments(): AthleteDocument[] {
    return screen.application().athletes.find((item) => item.id === athlete.id)?.documents ?? [];
  }

  /** Строки диалога: по одной на каждый тип словаря, с файлом из модели экрана. */
  function currentRows(): DocumentRow[] {
    const uploaded = new Map(savedDocuments().map((doc) => [doc.docId, doc]));
    return DOCUMENTS.map((type) => ({
      docId: type.id,
      title: type.title,
      uploaded: uploaded.get(type.id),
    }));
  }

  /** Перерисовывает таблицу из модели экрана (вернуться к ответу сервера после ошибки). */
  function render(): void {
    tbody.replaceChildren(...currentRows().map(renderRow));
  }

  /** Строка таблицы: тип документа, файл, размер с датой и действия. */
  function renderRow(row: DocumentRow): HTMLTableRowElement {
    return el('tr', {
      children: [
        cell('Название документа', el('span', { text: row.title })),
        cell(
          'Имя файла',
          row.uploaded
            ? el('span', { text: row.uploaded.uploadedFileName || '—' })
            : el('span', { className: 'text-muted', text: 'файл не загружен' }),
        ),
        cell(
          'Размер и дата',
          el('span', {
            text: row.uploaded
              ? `${fileSizeText(row.uploaded.size)} · ${uploadedAtText(row.uploaded.uploadedAt)}`
              : '—',
          }),
        ),
        cell('Действия', rowActions(row)),
      ],
    });
  }

  /** Кнопки строки: без файла — «Загрузить», с файлом — «Скачать», «Заменить» и «×». */
  function rowActions(row: DocumentRow): Node {
    const controls: Node[] = [];
    if (row.uploaded) {
      controls.push(button({ text: 'Скачать', onClick: () => void download(row) }));
    }
    controls.push(
      button({ text: row.uploaded ? 'Заменить' : 'Загрузить', onClick: () => startUpload(row) }),
    );
    if (row.uploaded) {
      controls.push(
        iconButton({
          icon: 'trash',
          variant: 'danger',
          title: `Удалить документ «${row.title}»`,
          onClick: () => void remove(row),
        }),
      );
    }
    return el('div', { className: 'row-view__actions', children: controls });
  }
  /**
   * Открывает выбор файла. Диалог выбора вызывается синхронно из клика (без `await`),
   * иначе браузер может счесть его самовольным окном; ожидание настроек перенесено в
   * обработчик выбора файла.
   */
  function startUpload(row: DocumentRow): void {
    status.clearError();
    const input = el('input', { attrs: { type: 'file', hidden: true } });
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      input.remove();
      if (file) void sendFile(row, file);
    });
    document.body.append(input);
    input.click();
  }

  /** Отправляет выбранный файл: сначала проверка лимитов, затем сценарий режима. */
  async function sendFile(row: DocumentRow, file: File): Promise<void> {
    status.pending();
    try {
      const config = await documentsConfig();
      const problem = fileProblem(file, config);
      // Проверка на клиенте повторяет серверную: незачем отправлять 10 МБ, чтобы
      // получить отказ, — но окончательное решение всё равно за сервером.
      if (problem) throw new ApiError(problem, 0);

      const { application } = await api.uploadAthleteDocument(
        athlete.id,
        row.docId,
        file,
        config.mode,
      );
      screen.apply(application);
      status.saved();
      render();
    } catch (err) {
      status.error(err);
      // Метаданные документа в заявке могли не измениться — показываем ответ сервера.
      render();
      // Истёкшая сессия уводит на экран входа: диалог за ним оставаться не должен.
      if (err instanceof ApiError && err.status === 401) dialog.close();
    }
  }

  /** Скачивает файл документа (сервер решает: файл в ответе или подписанная ссылка). */
  async function download(row: DocumentRow): Promise<void> {
    status.clearError();
    try {
      const config = await documentsConfig();
      await api.downloadAthleteDocument(athlete.id, row.docId, config.mode);
    } catch (err) {
      status.error(err);
    }
  }

  /** Удаляет файл документа после подтверждения: строка снова «файл не загружен». */
  async function remove(row: DocumentRow): Promise<void> {
    const confirmed = await confirmDialog(`Удалить документ «${row.title}»?`, 'Удалить');
    if (!confirmed) return;

    status.pending();
    try {
      const { application } = await api.deleteAthleteDocument(athlete.id, row.docId);
      screen.apply(application);
      status.saved();
      render();
    } catch (err) {
      status.error(err);
      render();
      if (err instanceof ApiError && err.status === 401) dialog.close();
    }
  }

  dialog.addEventListener('close', () => {
    dialog.remove();
    // Счётчик в строке спортсмена берёт число документов из модели экрана.
    refresh();
  });
  document.body.append(dialog);
  render();
  // Настройки нужны к первому клику по «Загрузить»: подтягиваем заранее, а сбой показываем.
  void documentsConfig().catch((err: unknown) => status.error(err));
  dialog.showModal();
}

/** Проверка файла по лимитам сервера: размер и тип (белый список). */
function fileProblem(file: File, config: api.DocumentsConfig): string | undefined {
  if (file.size > config.maxSizeBytes) {
    return `Файл больше ${Math.round(config.maxSizeBytes / (1024 * 1024))} МБ`;
  }
  if (!config.allowedTypes.includes(file.type)) {
    return 'Документ принимается в формате PDF, JPEG, PNG, WebP или HEIC';
  }
  return undefined;
}

/** Колонки таблицы спортсменов (сценарий 3 docs/spec.md). */
const ATHLETE_COLUMNS: RowColumn<Athlete>[] = [
  {
    label: 'ФИО',
    view: (row) => textView(row.fullName),
    control: (row) => textControl(row.fullName),
  },
  {
    label: 'Дата рождения',
    view: (row) => textView(row.birthDate),
    control: (row) => dateControl(row.birthDate),
  },
  {
    label: 'Пол',
    view: (row) => textView(GENDER_LABELS[row.gender]),
    control: (row) => selectControl(GENDERS, row.gender),
  },
  {
    label: 'Размер футболки',
    view: (row) => textView(row.tshirtSize),
    control: (row) => selectControl(tshirtOptions(), row.tshirtSize),
  },
  // Вычисляемые колонки: в редактировании не участвуют (контрола нет).
  {
    label: 'Виды программы',
    // Счётчик — гиперссылка: открывает диалог со строками «вид программы».
    view: (row, view) =>
      counterView(row.disciplines.length, 'Виды программы', () =>
        openDisciplinesDialog(view.screen, row, view.refresh),
      ),
  },
  {
    label: 'Документы',
    // Счётчик — гиперссылка: открывает диалог со строками «тип документа».
    view: (row, view) =>
      counterView(row.documents.length, 'Документы', () =>
        openDocumentsDialog(view.screen, row, view.refresh),
      ),
  },
];

/** Колонки таблицы представителей. */
const REPRESENTATIVE_COLUMNS: RowColumn<Representative>[] = [
  {
    label: 'ФИО',
    view: (row) => textView(row.fullName),
    control: (row) => textControl(row.fullName),
  },
  {
    label: 'Размер футболки',
    view: (row) => textView(row.tshirtSize),
    control: (row) => selectControl(tshirtOptions(), row.tshirtSize),
  },
];

/** Секция «Спортсмены»: строки со спортсменами и их видами программы. */
function athletesSection(context: ScreenContext): HTMLElement {
  return rowsSection<Athlete>(context, {
    title: 'Спортсмены',
    addTitle: 'Добавить спортсмена',
    columns: ATHLETE_COLUMNS,
    // В карточке под ячейкой действий только иконки (карандаш и корзина): подпись
    // «Действия» не нужна, строка карточки остаётся короче (замечание организатора).
    hideActionsLabel: true,
    rows: (screen) => screen.application().athletes,
    draft: () => ({
      id: ADD_ROW_ID,
      fullName: '',
      birthDate: '',
      gender: 'M',
      tshirtSize: 'M',
      disciplines: [],
      documents: [],
    }),
    fromValues: (values, existing) => ({
      id: existing.id,
      fullName: values[0],
      birthDate: values[1],
      gender: values[2] as Gender,
      tshirtSize: values[3] as TshirtSize,
      // Столбца «Виды программы» в таблице нет: их ведёт диалог, а строка их сохраняет.
      disciplines: existing.disciplines,
      // Документы тоже не столбец: файлами управляет диалог, строка их сохраняет.
      documents: existing.documents,
    }),
    create: async (row) => (await api.createAthlete(row)).application,
    update: async (row) => (await api.updateAthlete(row)).application,
    remove: async (id) => (await api.deleteAthlete(id)).application,
  });
}

/**
 * Поле секции «Представители», которое относится к секции целиком, а не к строке
 * таблицы: сколько ещё сопровождающих едет с командой (друзья, родители) — кроме
 * спортсменов и перечисленных представителей. Правка организатора 15.10.2026:
 * раньше состав команды ограничивался строками таблицы. Значение по умолчанию — 0.
 */
const REPRESENTATIVES_ACCOMPANYING_FIELD: RowsSectionNumberField = {
  id: 'representatives-accompanying',
  label: 'Количество остальных сопровождающих',
  value: (application) => application.accompanyingCount,
  save: async (value) => (await api.saveAccompanyingCount(value)).application,
};

/** Секция «Представители»: строки представителей, которые едут с командой. */
function representativesSection(context: ScreenContext): HTMLElement {
  return rowsSection<Representative>(context, {
    title: 'Представители',
    addTitle: 'Добавить представителя',
    columns: REPRESENTATIVE_COLUMNS,
    numberField: REPRESENTATIVES_ACCOMPANYING_FIELD,
    // Та же причина, что и у спортсменов: в карточке под ячейкой действий только иконки
    // (карандаш и корзина), подпись «Действия» отнимала у карточки строку.
    hideActionsLabel: true,
    rows: (screen) => screen.application().representatives,
    draft: () => ({ id: ADD_ROW_ID, fullName: '', tshirtSize: 'M' }),
    fromValues: (values, existing) => ({
      id: existing.id,
      fullName: values[0],
      tshirtSize: values[1] as TshirtSize,
    }),
    create: async (row) => (await api.createRepresentative(row)).application,
    update: async (row) => (await api.updateRepresentative(row)).application,
    remove: async (id) => (await api.deleteRepresentative(id)).application,
  });
}

/** Подписи пустых вариантов: тип и первые два спортсмена обязательны, третий — нет. */
const DUILIAN_TYPE_PLACEHOLDER = '— выберите тип —';
const DUILIAN_ATHLETE_PLACEHOLDER = '— выберите спортсмена —';
const DUILIAN_OPTIONAL_ATHLETE_PLACEHOLDER = '— не указан —';

/**
 * Подсказка в подвале секции «Дуйлянь» (правка организатора 18.10.2026). Правило
 * «в выступлении участвуют только заявленные на «Дуйлянь»» видно лишь по пустым
 * дропдаунам спортсменов, поэтому о нём говорит эта строка. Диалог назван так, как он
 * подписан в интерфейсе: «Виды программы» — там «Дуйлянь» и добавляют спортсмену.
 */
const DUILIAN_ATHLETES_HINT =
  'Можно выбрать только спортсменов, которым добавлен «Дуйлянь» в список выступлений (диалог «Виды программы»).';

/** Колонка «Тип» идёт первой: все следующие колонки строки — дропдауны спортсменов. */
const DUILIAN_FIRST_ATHLETE_COLUMN = 1;

/** Участник по значению дропдауна; пустой id — спортсмен ещё не выбран. */
function participantById(athleteId: string): DuilianParticipant {
  return { id: athleteId, fullName: '' };
}

/** Колонка спортсмена строки: подпись, признак необязательности и чтение участника. */
interface DuilianParticipantColumn {
  label: string;
  /** Третий спортсмен необязателен: в его дропдауне есть выбираемый пустой вариант. */
  optional: boolean;
  participant(row: DuilianEntry): DuilianParticipant | null;
}

/** Колонки спортсменов в порядке ТЗ: два обязательных и необязательный третий. */
const DUILIAN_PARTICIPANT_COLUMNS: DuilianParticipantColumn[] = [
  { label: 'Спортсмен 1', optional: false, participant: (row) => row.athlete1 },
  { label: 'Спортсмен 2', optional: false, participant: (row) => row.athlete2 },
  { label: 'Спортсмен 3', optional: true, participant: (row) => row.athlete3 },
];

/** Варианты дропдауна «Тип»: виды программы «Дуйлянь» из справочника. */
function duilianTypeOptions(): SelectOption[] {
  return [
    { value: '', label: DUILIAN_TYPE_PLACEHOLDER, disabled: true },
    ...DUILIAN_DISCIPLINES.map((discipline) => ({
      value: discipline.id,
      label: discipline.name,
    })),
  ];
}

/**
 * Идентификаторы спортсменов, уже стоящих в строке: они остаются в её дропдаунах
 * даже тогда, когда «Дуйлянь» спортсмену ещё не добавлен (строка сохранена раньше
 * или вид программы убрали из диалога «Выступления»). Иначе при открытии правки
 * дропдаун показал бы чужое значение или пустоту, а «галочка» затёрла бы сохранённого
 * спортсмена — правка старой строки не должна терять данные.
 */
function duilianParticipantIds(row: DuilianEntry): Set<string> {
  const ids = DUILIAN_PARTICIPANT_COLUMNS.map((column) => column.participant(row)?.id ?? '');
  return new Set(ids.filter((id) => id !== ''));
}

/**
 * Варианты дропдауна спортсмена: ФИО спортсменов заявки, которым добавлен хотя бы один
 * вид программы «Дуйлянь» — в парном или групповом выступлении участвуют только
 * заявленные на «Дуйлянь» (правка организатора 18.10.2026, отбор — общий
 * `hasDuilianDiscipline`). Отдельно остаются спортсмены, уже стоящие в этой строке
 * (`keepIds`): их правка не теряет.
 *
 * У третьего спортсмена пустой вариант выбирается («выступают вдвоём»), у первых двух
 * он только подсказка. Уже выбранных в этой же строке закрывает `linkAthleteOptions` —
 * вариант виден, но выбрать его нельзя.
 */
function athleteOptions(
  context: ScreenContext,
  optional: boolean,
  keepIds: ReadonlySet<string>,
): SelectOption[] {
  const athletes = context
    .application()
    .athletes.filter((athlete) => hasDuilianDiscipline(athlete) || keepIds.has(athlete.id));
  return [
    {
      value: '',
      label: optional ? DUILIAN_OPTIONAL_ATHLETE_PLACEHOLDER : DUILIAN_ATHLETE_PLACEHOLDER,
      disabled: !optional,
    },
    ...athletes.map((athlete) => ({
      value: athlete.id,
      label: athlete.fullName,
    })),
  ];
}

/**
 * Колонки таблицы «Дуйляня»: тип выступления и спортсмены. Список спортсменов — из
 * секции «Спортсмены», поэтому дропдауны строятся по текущей модели экрана:
 * добавленный спортсмен сразу доступен в выступлениях, если ему добавлен «Дуйлянь».
 */
function duilianColumns(context: ScreenContext): RowColumn<DuilianEntry>[] {
  return [
    {
      label: 'Тип',
      view: (row) => textView(row.disciplineName),
      control: (row) => selectControl(duilianTypeOptions(), row.disciplineId),
    },
    ...DUILIAN_PARTICIPANT_COLUMNS.map((column) => ({
      label: column.label,
      view: (row: DuilianEntry) => textView(column.participant(row)?.fullName ?? ''),
      control: (row: DuilianEntry) =>
        selectControl(
          athleteOptions(context, column.optional, duilianParticipantIds(row)),
          column.participant(row)?.id ?? '',
        ),
    })),
  ];
}

/**
 * Связывает дропдауны спортсменов строки (хук `linkRow` секции строк): ФИО, выбранные
 * в одном дропдауне, закрываются в остальных — второй спортсмен выбирается из всех,
 * кроме первого, третий — кроме первых двух, и один спортсмен не может выступать в
 * строке дважды (сервер отклоняет повторы).
 */
function linkAthleteOptions(controls: (FieldControl | null)[]): void {
  const selects = controls
    .slice(DUILIAN_FIRST_ATHLETE_COLUMN)
    .filter((control): control is HTMLSelectElement => control instanceof HTMLSelectElement);
  for (const select of selects) {
    select.addEventListener('change', () => markTakenOptions(selects));
  }
  markTakenOptions(selects);
}

/** Поля строки для API: вид программы и спортсмены передаются идентификаторами. */
function duilianFields(row: DuilianEntry): api.DuilianFields {
  return {
    disciplineId: row.disciplineId,
    athlete1Id: row.athlete1.id,
    athlete2Id: row.athlete2.id,
    athlete3Id: row.athlete3?.id ?? '',
  };
}

/**
 * Секция «Дуйлянь»: выступления на двух или трёх спортсменов, строка — одно
 * выступление. Раскладка — как у секции «Спортсмены»: её строит та же фабрика
 * `rowsSection` (таблица, на узком экране строки становятся карточками, в конце
 * таблицы кнопка «+», в подвале секции — подсказка о составе дропдаунов). Название
 * вида программы и ФИО в запрос не идут: после сохранения их возвращает сервер,
 * поэтому в просмотре всегда видно сохранённое.
 */
function duilianSection(context: ScreenContext): HTMLElement {
  return rowsSection<DuilianEntry>(context, {
    title: 'Дуйлянь',
    addTitle: 'Добавить выступление',
    columns: duilianColumns(context),
    // В карточке под ячейкой действий только иконки (карандаш и корзина) — как у спортсменов.
    hideActionsLabel: true,
    // Правило видно только по пустым дропдаунам, поэтому о нём говорит подсказка секции.
    hint: DUILIAN_ATHLETES_HINT,
    rows: (screen) => screen.application().duilian,
    linkRow: linkAthleteOptions,
    draft: () => ({
      id: ADD_ROW_ID,
      disciplineId: '',
      disciplineName: '',
      athlete1: participantById(''),
      athlete2: participantById(''),
      athlete3: null,
    }),
    fromValues: (values, existing) => {
      // Третий спортсмен необязателен: пустое значение дропдауна — «выступают вдвоём».
      const third = values[3] ?? '';
      return {
        id: existing.id,
        disciplineId: values[0],
        // Название и ФИО считает сервер по идентификаторам: из строки экрана они не читаются.
        disciplineName: existing.disciplineName,
        athlete1: participantById(values[1]),
        athlete2: participantById(values[2]),
        athlete3: third === '' ? null : participantById(third),
      };
    },
    create: async (row) => (await api.createDuilianEntry(duilianFields(row))).application,
    update: async (row) =>
      (await api.updateDuilianEntry({ ...duilianFields(row), id: row.id })).application,
    remove: async (id) => (await api.deleteDuilianEntry(id)).application,
  });
}


